import TemplateDateFlightsPicker, {
  TEMPLATE_DATE_PICKER_NESTED_MODAL_Z_INDEX,
} from "../pages/SendEmail/TemplateDateFlightsPicker";
import type { IFilterData } from "../components/Organismos/Table/FilteredTable.types";
import type { TemplateDateFilterValue } from "./templateDateFilterTypes";
import {
  AUDIT_LOG_DATE_QUERY_FIELDS,
  auditLogDateFilterChips,
  buildAuditLogDateFilter,
  createDefaultAuditLogDateFilter,
  defaultAuditLogDateQueryClauses,
} from "./auditLogDateFilters";

const DEFAULT_AUDIT_LOG_DATE_FILTER = createDefaultAuditLogDateFilter();

export type AuditLogDateFilterPanelOptions = {
  name?: string;
  customTitle?: string;
  label?: string;
  dialogTitle?: string;
  /** When the filter is rendered inside a Modal (raises date picker portal above it). */
  nestedInModal?: boolean;
  /** Hides the filter label above the date control (firm logs toolbar). */
  hideLabel?: boolean;
  /** Picker UI variant; `firmLogs` renders separate From/To fields for the logs modal. */
  pickerVariant?: "catalog" | "firmLogs";
};

/** Ready-to-use `filtersData` entry for audit/access log date ranges. */
export function createAuditLogDateFilterPanel(
  options: AuditLogDateFilterPanelOptions = {},
): IFilterData {
  const {
    name = "Date",
    customTitle = "Date",
    label = "By date:",
    dialogTitle = "Audit log date",
    nestedInModal = false,
    hideLabel = false,
    pickerVariant = "catalog",
  } = options;

  const portalZIndex = nestedInModal
    ? TEMPLATE_DATE_PICKER_NESTED_MODAL_Z_INDEX
    : undefined;

  return {
    name,
    type: "custom",
    customTitle,
    panelColSpan: "wide",
    defaultCustomFilterValue: DEFAULT_AUDIT_LOG_DATE_FILTER,
    customQueryFieldNames: [...AUDIT_LOG_DATE_QUERY_FIELDS],
    getDefaultQueryClauses: defaultAuditLogDateQueryClauses,
    getCustomSelectedChips: auditLogDateFilterChips,
    render: ({ value, apply }) => (
      <TemplateDateFlightsPicker
        value={
          (value as TemplateDateFilterValue | null | undefined) ??
          DEFAULT_AUDIT_LOG_DATE_FILTER
        }
        onApply={(next) => {
          apply(
            next,
            buildAuditLogDateFilter(
              next.templateDatePreset,
              next.templateDateFrom,
              next.templateDateTo,
            ) ?? null,
          );
        }}
        variant={pickerVariant}
        label={label}
        dialogTitle={dialogTitle}
        showYearPresets={false}
        portalZIndex={portalZIndex}
        hideLabel={hideLabel || pickerVariant === "firmLogs"}
      />
    ),
  };
}

/** Default audit log date filter (current month through today, no year shortcuts). */
export const AUDIT_LOG_DATE_FILTER_PANEL = createAuditLogDateFilterPanel();
